import {
  Hotel,
  BedDouble,
  Building2,
  Landmark,
  Flag,
  Waves,
  Anchor,
  Utensils,
  Coffee,
  Wine,
  CakeSlice,
  ChefHat,
  Trophy,
  Shirt,
  Gem,
  Factory,
  Wrench,
  Cpu,
  Wheat,
  TreePine,
  Sofa,
  Lamp,
  Briefcase,
  GraduationCap,
  Truck,
  Plane,
  Car,
  Sparkles,
  Heart,
  Palette,
  Ticket,
  Store,
  Home,
  Printer,
  FlaskConical,
  Fuel,
  Shield,
  type LucideIcon,
} from "lucide-react";

export interface CategoryConfig {
  color: string;
  icon: LucideIcon;
  label: string;
}

// ============================================
// CANONICAL TYPOLOGY CONFIGURATION
// Only includes the normalized/canonical typologies
// that remain after import-time normalization
// Note: Labels in config may be translated, but getTypologyConfig() 
// always returns the original key as label
// ============================================
// Mapping of typologies to their macro categories
// This ensures typologies use colors from the same palette as their macro category
// Using original values from database - no normalization
const typologyToCategoryMap: Record<string, string> = {
  // Accomodation (original value from database)
  Accommodation: "Accomodation",
  Accomodation: "Accomodation", // Original value from database
  Hotel: "Accomodation", // Map Hotel to Accomodation as per requirement
  "Hotel Storico": "Accomodation",
  Locanda: "Accomodation",
  Hospitality: "Accomodation", // Map Hospitality to Accomodation

  // DMO
  DMO: "DMO",
  "Convention Bureau": "DMO",
  APT: "DMO",
  ATL: "DMO",
  OGD: "DMO",
  Consorzio: "DMO",
  DMC: "DMO",

  // Sport
  "Golf Club": "Sport",
  "Circolo Velico": "Sport",
  "Circolo Nautico": "Sport",
  "Lega Navale": "Sport",
  "Yacht Club": "Sport",
  "Associazione Sportiva": "Sport",
  CUS: "Sport",
  "Ente Sportivo Militare": "Sport",
  "Sport, Hobby, Intrattenimento, Arte": "Sport",
  "Sport & Tempo Libero": "Sport",

  // Bandiera Blu
  "Comune/Spiaggia Bandiera Blu": "Bandiera Blu",
  "Approdo Bandiera Blu": "Bandiera Blu",

  // Locali Storici
  Ristorante: "Locali Storici",
  "Ristorante Storico": "Locali Storici",
  Trattoria: "Locali Storici",
  "Trattoria Storica": "Locali Storici",
  Osteria: "Locali Storici",
  "Osteria Storica": "Locali Storici",
  "Caffè Storico": "Locali Storici",
  "Caffè Pasticceria": "Locali Storici",
  "Pasticceria Storica": "Locali Storici",
  "Cioccolateria Storica": "Locali Storici",
  Enogastronomia: "Locali Storici",
  Enogastronomico: "Locali Storici",
  "Food, Bevande, Ospitalità": "Locali Storici",
  "Teeria Storica": "Locali Storici",

  // Fiere & Eventi
  Attractions_Events: "Fiere & Eventi",
  "Agricoltura & Agroalimentare": "Fiere & Eventi",
  "Arte & Cultura": "Fiere & Eventi",
  "Industria & Meccanica": "Fiere & Eventi",
  "Arredo & Interior": "Fiere & Eventi",
  Gioielleria: "Fiere & Eventi",
  "Pop Culture & Fumetti": "Fiere & Eventi",
  "Costruzioni & Infrastrutture": "Fiere & Eventi",
  "General Fair": "Fiere & Eventi",
  Automotive: "Fiere & Eventi",
  "Antiquariato & Collezionismo": "Fiere & Eventi",
  "Bellezza & Cosmesi": "Fiere & Eventi",
  "Elettronica & Componentistica": "Fiere & Eventi",
  "Ambiente & Sostenibilità": "Fiere & Eventi",
  "Business & Services": "Fiere & Eventi",
  "Tecnologia & Innovazione": "Fiere & Eventi",
  "Turismo & Viaggi": "Fiere & Eventi",
  "Lifestyle & Creatività": "Fiere & Eventi",
  "Marittime & Nautica": "Fiere & Eventi",
  "Formazione & Education": "Fiere & Eventi",
  "Artigianato & Design": "Fiere & Eventi",
  "Salute & Medicale": "Fiere & Eventi",
  "Packaging & Processing": "Fiere & Eventi",
  Pet: "Fiere & Eventi",
  "Home & Gift": "Fiere & Eventi",
  "Sicurezza & Difesa": "Fiere & Eventi",
  "Energia & Impianti": "Fiere & Eventi",
  "Giardinaggio & Arredo Verde": "Fiere & Eventi",
  "Trasporti & Logistica": "Fiere & Eventi",
  Wedding: "Fiere & Eventi",
  "Famiglia & Bambini": "Fiere & Eventi",
  Lifestyle: "Fiere & Eventi",
  "Ottica & Optometria": "Fiere & Eventi",
  "Brico & Fai-da-te": "Fiere & Eventi",
  "Hobby & Intrattenimento": "Fiere & Eventi",
  "Lifestyle & Gift": "Fiere & Eventi",
  "Hospitality & Ristorazione": "Fiere & Eventi",
};

// Color palettes for each macro category
// Each palette contains variations of the base color
const categoryColorPalettes: Record<string, string[]> = {
  Hospitality: ["#284899", "#1e3570", "#15254d", "#4a6fa5", "#6b8fc7"], // blue from logo
  DMO: ["#3b82f6", "#2563eb", "#1d4ed8", "#60a5fa", "#93c5fd"], // blue
  Sport: ["#10b981", "#059669", "#047857", "#34d399", "#6ee7b7"], // emerald/green
  "Bandiera Blu": ["#06b6d4", "#0891b2", "#0e7490", "#22d3ee", "#67e8f9"], // cyan
  "Fiere & Eventi": ["#f472b6", "#ec4899", "#db2777", "#e879f9", "#d946ef"], // pink/rose (medium-dark variants for better readability)
  "Locali Storici": ["#1e3570", "#15254d", "#0f1a33", "#284899", "#4a6fa5"], // blue from logo (darker)
  "Attractions_Events": ["#f472b6", "#ec4899", "#db2777", "#e879f9", "#d946ef"], // pink/rose (medium-dark variants for better readability)
  "Non specificato": ["#64748b", "#475569", "#334155", "#94a3b8", "#cbd5e1"], // slate/gray
};

// Get a color from the category's palette based on typology name hash
function getColorFromPalette(typology: string, macroCategory: string): string {
  const palette = categoryColorPalettes[macroCategory] || categoryColorPalettes["Non specificato"];
  // Use hash of typology name to consistently pick a color from palette
  let hash = 0;
  for (let i = 0; i < typology.length; i++) {
    hash = typology.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % palette.length;
  return palette[index];
}

export const typologyConfig: Record<string, CategoryConfig> = {
  // 🏨 ACCOMMODATION (blue from logo) - macro: Hospitality
  Accommodation: { color: "#284899", icon: BedDouble, label: "Accommodation" },
  Accomodation: { color: "#1e3570", icon: BedDouble, label: "Accomodation" }, // Typo variant
  Hotel: { color: "#1e3570", icon: Hotel, label: "Hotel" },
  "Hotel Storico": { color: "#15254d", icon: Hotel, label: "Historic Hotel" },
  Locanda: { color: "#4a6fa5", icon: Home, label: "Inn" },
  Hospitality: { color: "#284899", icon: BedDouble, label: "Hospitality" },

  // 🏛️ TOURISM/DMO (blue) - macro: DMO
  DMO: { color: "#3b82f6", icon: Landmark, label: "DMO" },
  "Convention Bureau": { color: "#3b82f6", icon: Landmark, label: "Convention Bureau" },
  APT: { color: "#3b82f6", icon: Landmark, label: "APT" },
  ATL: { color: "#3b82f6", icon: Landmark, label: "ATL" },
  OGD: { color: "#3b82f6", icon: Landmark, label: "OGD" },
  Consorzio: { color: "#3b82f6", icon: Landmark, label: "Consortium" },
  DMC: { color: "#3b82f6", icon: Landmark, label: "DMC" },

  // 🏌️ SPORT (green/emerald) - macro: Sport
  "Golf Club": { color: "#10b981", icon: Flag, label: "Golf Club" },
  "Circolo Velico": { color: "#10b981", icon: Waves, label: "Sailing Club" },
  "Circolo Nautico": { color: "#10b981", icon: Anchor, label: "Yacht Club" },
  "Lega Navale": { color: "#10b981", icon: Anchor, label: "Naval League" },
  "Yacht Club": { color: "#10b981", icon: Anchor, label: "Yacht Club" },
  "Associazione Sportiva": { color: "#10b981", icon: Trophy, label: "Sports Association" },
  CUS: { color: "#10b981", icon: Trophy, label: "CUS" },
  "Ente Sportivo Militare": { color: "#10b981", icon: Trophy, label: "Military Sports Organization" },

  // 🌊 BLUE FLAGS (cyan) - macro: Bandiera Blu
  "Comune/Spiaggia Bandiera Blu": { color: "#06b6d4", icon: Waves, label: "Blue Flag Beach" },
  "Approdo Bandiera Blu": { color: "#06b6d4", icon: Anchor, label: "Blue Flag Marina" },

  // 🍽️ RESTAURANTS & FOOD (blue from logo) - macro: Locali Storici
  Ristorante: { color: "#1e3570", icon: Utensils, label: "Restaurant" },
  "Ristorante Storico": { color: "#1e3570", icon: Utensils, label: "Historic Restaurant" },
  Trattoria: { color: "#1e3570", icon: ChefHat, label: "Trattoria" },
  "Trattoria Storica": { color: "#1e3570", icon: ChefHat, label: "Historic Trattoria" },
  Osteria: { color: "#1e3570", icon: Wine, label: "Osteria" },
  "Osteria Storica": { color: "#1e3570", icon: Wine, label: "Historic Osteria" },
  "Caffè Storico": { color: "#1e3570", icon: Coffee, label: "Historic Café" },
  "Caffè Pasticceria": { color: "#1e3570", icon: Coffee, label: "Café & Pastry Shop" },
  "Pasticceria Storica": { color: "#1e3570", icon: CakeSlice, label: "Historic Pastry Shop" },
  "Cioccolateria Storica": { color: "#1e3570", icon: CakeSlice, label: "Historic Chocolate Shop" },
  Enogastronomia: { color: "#1e3570", icon: Wine, label: "Food & Wine" },
  Enogastronomico: { color: "#1e3570", icon: Wine, label: "Food & Wine" },
  "Food, Bevande, Ospitalità": { color: "#1e3570", icon: Utensils, label: "Food & Hospitality" },
  "Teeria Storica": { color: "#1e3570", icon: Coffee, label: "Historic Tea Room" },

  // 🎮 SPORTS & ENTERTAINMENT (green/emerald) - macro: Sport
  "Sport, Hobby, Intrattenimento, Arte": { color: "#10b981", icon: Trophy, label: "Sports & Entertainment" },
  "Sport & Tempo Libero": { color: "#10b981", icon: Trophy, label: "Sports & Leisure" },

  // 🎪 FAIRS & EVENTS (pink/rose) - macro: Fiere & Eventi
  Attractions_Events: { color: "#f472b6", icon: Ticket, label: "Attractions & Events" },
  "Agricoltura & Agroalimentare": { color: "#f472b6", icon: Wheat, label: "Agriculture & Food" },
  "Arte & Cultura": { color: "#f472b6", icon: Palette, label: "Art & Culture" },
  "Industria & Meccanica": { color: "#f472b6", icon: Factory, label: "Industry & Mechanics" },
  "Arredo & Interior": { color: "#f472b6", icon: Sofa, label: "Furniture & Interior" },
  Gioielleria: { color: "#f472b6", icon: Gem, label: "Jewelry" },
  "Pop Culture & Fumetti": { color: "#f472b6", icon: Ticket, label: "Pop Culture & Comics" },
  "Costruzioni & Infrastrutture": { color: "#f472b6", icon: Building2, label: "Construction & Infrastructure" },
  "General Fair": { color: "#f472b6", icon: Ticket, label: "General Fair" },
  Automotive: { color: "#f472b6", icon: Car, label: "Automotive" },
  "Antiquariato & Collezionismo": { color: "#f472b6", icon: Store, label: "Antiques & Collectibles" },
  "Bellezza & Cosmesi": { color: "#f472b6", icon: Sparkles, label: "Beauty & Cosmetics" },
  "Elettronica & Componentistica": { color: "#f472b6", icon: Cpu, label: "Electronics & Components" },
  "Ambiente & Sostenibilità": { color: "#db2777", icon: TreePine, label: "Environment & Sustainability" },
  "Business & Services": { color: "#f472b6", icon: Briefcase, label: "Business & Services" },
  "Tecnologia & Innovazione": { color: "#f472b6", icon: Cpu, label: "Technology & Innovation" },
  "Turismo & Viaggi": { color: "#f472b6", icon: Plane, label: "Tourism & Travel" },
  "Lifestyle & Creatività": { color: "#f472b6", icon: Sparkles, label: "Lifestyle & Creatività" },
  "Marittime & Nautica": { color: "#f472b6", icon: Anchor, label: "Maritime & Nautical" },
  "Formazione & Education": { color: "#f472b6", icon: GraduationCap, label: "Education & Training" },
  "Artigianato & Design": { color: "#f472b6", icon: Wrench, label: "Craftsmanship & Design" },
  "Salute & Medicale": { color: "#f472b6", icon: Heart, label: "Health & Medical" },
  "Packaging & Processing": { color: "#f472b6", icon: Printer, label: "Packaging & Processing" },
  Pet: { color: "#f472b6", icon: Heart, label: "Pet" },
  "Home & Gift": { color: "#f472b6", icon: Lamp, label: "Home & Gift" },
  "Sicurezza & Difesa": { color: "#f472b6", icon: Shield, label: "Security & Defense" },
  "Energia & Impianti": { color: "#f472b6", icon: Fuel, label: "Energy & Plants" },
  "Giardinaggio & Arredo Verde": { color: "#f472b6", icon: TreePine, label: "Gardening & Green Design" },
  "Trasporti & Logistica": { color: "#f472b6", icon: Truck, label: "Transport & Logistics" },
  Wedding: { color: "#f472b6", icon: Sparkles, label: "Wedding" },
  "Famiglia & Bambini": { color: "#f472b6", icon: Home, label: "Family & Children" },
  Lifestyle: { color: "#f472b6", icon: Sparkles, label: "Lifestyle" },
  "Ottica & Optometria": { color: "#f472b6", icon: Store, label: "Optics & Optometry" },
  "Brico & Fai-da-te": { color: "#f472b6", icon: Wrench, label: "DIY & Hardware" },
  "Hobby & Intrattenimento": { color: "#f472b6", icon: Trophy, label: "Hobby & Entertainment" },
  "Lifestyle & Gift": { color: "#f472b6", icon: Lamp, label: "Lifestyle & Gift" },
  "Hospitality & Ristorazione": { color: "#f472b6", icon: Utensils, label: "Hospitality & Restaurants" },

  // 👗 FASHION & TEXTILE (purple)
  "Tessile, Abbigliamento, Moda": { color: "#8b5cf6", icon: Shirt, label: "Fashion & Textile" },
  "Moda & Tendenze": { color: "#8b5cf6", icon: Shirt, label: "Fashion & Trends" },
  "Gioielli, Orologi, Accessori": { color: "#a855f7", icon: Gem, label: "Jewelry & Accessories" },

  // 🏭 INDUSTRY & TECH (slate) - per tipologie generali
  "Industria, Tecnologia, Meccanica": { color: "#64748b", icon: Factory, label: "Industry & Tech" },
  "Costruzioni, Infrastrutture": { color: "#78716c", icon: Building2, label: "Construction" },
  "Elettronica, Componenti": { color: "#57534e", icon: Cpu, label: "Electronics" },
  "IT e Telecomunicazioni": { color: "#6366f1", icon: Cpu, label: "IT & Telecom" },
  "Stampa, Packaging, Imballaggi": { color: "#78716c", icon: Printer, label: "Printing & Packaging" },
  Chimica: { color: "#71717a", icon: FlaskConical, label: "Chemistry" },
  "Energia, Combustibili, Gas": { color: "#f97316", icon: Fuel, label: "Energy & Fuels" },
  "Sicurezza, Antincendio, Difesa": { color: "#6366f1", icon: Shield, label: "Security & Safety" },

  // 🌿 AGRICULTURE (lime) - per tipologie generali
  "Agricoltura, Silvicoltura, Zootecnia": { color: "#84cc16", icon: Wheat, label: "Agriculture" },

  // 🛋️ FURNITURE & DESIGN (blue from logo) - per tipologie generali
  "Arredamento, Design d'interni": { color: "#1e3570", icon: Sofa, label: "Furniture & Design" },
  Artigianato: { color: "#78350f", icon: Wrench, label: "Craftsmanship" },
  "Casalinghi, Giochi, Regalistica": { color: "#4a6fa5", icon: Lamp, label: "Home & Gifts" },

  // 💼 SERVICES & BUSINESS (indigo)
  "Servizi Business, Commercio": { color: "#6366f1", icon: Briefcase, label: "Business Services" },
  "Formazione, Educazione": { color: "#818cf8", icon: GraduationCap, label: "Education & Training" },
  "Campionarie Generali": { color: "#a5b4fc", icon: Store, label: "Trade Fairs" },

  // 🚚 TRANSPORT (sky)
  "Trasporti, Logistica, Navigazione": { color: "#0ea5e9", icon: Truck, label: "Transport & Logistics" },
  "Viaggi, Trasporti": { color: "#0369a1", icon: Plane, label: "Travel & Transport" },
  "Automobili, Motocicli": { color: "#075985", icon: Car, label: "Automotive" },

  // 💄 BEAUTY & HEALTH (fuchsia)
  "Bellezza, Cosmetica": { color: "#d946ef", icon: Sparkles, label: "Beauty & Cosmetics" },
  "Salute, Attrezzature ospedaliere": { color: "#a21caf", icon: Heart, label: "Healthcare" },

  // 🎨 ART & CULTURE (red) - per tipologie generali
  "Arte / Cultura": { color: "#ef4444", icon: Palette, label: "Art & Culture" },

  // 🌳 ENVIRONMENT (teal)
  "Protezione dell'ambiente": { color: "#14b8a6", icon: TreePine, label: "Environment" },
};

// Default configuration for unknown typologies
export const defaultConfig: CategoryConfig = {
  color: "#64748b",
  icon: Store,
  label: "Other",
};

// Get config for a typology (with fallback)
// Always returns the original key as label (no translation)
// ALWAYS uses color from macro category's palette when macroCategory parameter is provided
// When macroCategory is provided, it takes priority and uses the parent category's palette
export function getTypologyConfig(
  typology: string | null | undefined,
  macroCategory?: string | null | undefined
): CategoryConfig {
  if (!typology) return defaultConfig;

  // If macro category is provided (from parent context), ALWAYS use its palette color
  // This ensures typologies automatically inherit colors from their parent macro category
  if (macroCategory && categoryConfig[macroCategory]) {
    const categoryConfigData = categoryConfig[macroCategory];
    const color = getColorFromPalette(typology, macroCategory);

    // Get icon from explicit typology config if available, otherwise use category icon
    const explicitConfig = typologyConfig[typology];
    const icon = explicitConfig?.icon || categoryConfigData.icon;

    return {
      color, // Always use palette color from parent macro category
      icon,
      label: typology,
    };
  }

  // Fallback: if no macro category provided, try mapping (for backward compatibility)
  const mappedCategory = typologyToCategoryMap[typology];
  if (mappedCategory && categoryConfig[mappedCategory]) {
    const categoryConfigData = categoryConfig[mappedCategory];
    const color = getColorFromPalette(typology, mappedCategory);
    const explicitConfig = typologyConfig[typology];
    const icon = explicitConfig?.icon || categoryConfigData.icon;

    return {
      color,
      icon,
      label: typology,
    };
  }

  // Final fallback: use explicit typology config if available
  const config = typologyConfig[typology] || defaultConfig;

  // Override label with original key to ensure no translation
  return { ...config, label: typology };
}

// Get just the color
// Always uses macro category palette if mapping exists
export function getTypologyColor(typology: string | null | undefined, macroCategory?: string | null | undefined): string {
  return getTypologyConfig(typology, macroCategory).color;
}

// Get just the icon component
export function getTypologyIcon(typology: string | null | undefined): LucideIcon {
  return getTypologyConfig(typology).icon;
}

// Get display label for typology - returns original value without translation
export function getTypologyLabel(typology: string | null | undefined): string {
  if (!typology) return "";
  // Return original value directly without translation
  return typology;
}

// ============================================
// MACRO CATEGORY CONFIGURATION
// ============================================
export const categoryConfig: Record<string, CategoryConfig> = {
  Accomodation: { color: "#284899", icon: BedDouble, label: "Accomodation" }, // Original value from database
  Hospitality: { color: "#284899", icon: BedDouble, label: "Hospitality" }, // Keep for backward compatibility
  Attractions_Events: { color: "#f472b6", icon: Ticket, label: "Attractions & Events" },
  Sport: { color: "#10b981", icon: Trophy, label: "Sport" },
  "Bandiera Blu": { color: "#06b6d4", icon: Waves, label: "Bandiera Blu" },
  "Fiere & Eventi": { color: "#f472b6", icon: Ticket, label: "Fiere & Eventi" },
  "Locali Storici": { color: "#1e3570", icon: Utensils, label: "Locali Storici" }, // Original value
  DMO: { color: "#3b82f6", icon: Landmark, label: "DMO" },
  "Organizzazioni Turistiche": { color: "#3b82f6", icon: Landmark, label: "Organizzazioni Turistiche" }, // Original value
  "Non specificato": { color: "#64748b", icon: Store, label: "Non specificato" },
};

// Get config for a macro_category (with fallback)
export function getCategoryConfig(category: string | null | undefined): CategoryConfig {
  if (!category) return defaultConfig;
  return categoryConfig[category] || defaultConfig;
}

// Macro category labels
// Using original values from database - no translation
export const categoryLabels: Record<string, string> = {
  Accomodation: "Accomodation", // Original value from database
  Hospitality: "Hospitality", // Keep for backward compatibility
  Attractions_Events: "Attractions_Events",
  Sport: "Sport",
  "Bandiera Blu": "Bandiera Blu", // Original value
  "Fiere & Eventi": "Fiere & Eventi", // Original value
  "Locali Storici": "Locali Storici", // Original value
  DMO: "DMO", // Original value
  "Organizzazioni Turistiche": "Organizzazioni Turistiche", // Original value
  "Non specificato": "Non specificato", // Original value
};

// Get display label for macro_category - returns original value without translation
export function getCategoryLabel(category: string | null | undefined): string {
  if (!category) return "Non specificato";
  // Return original value directly without translation
  return category;
}

// Get color for typology based on its macro category (if available)
// Falls back to typology-specific color if macro category not available
export function getTypologyColorByCategory(
  typology: string | null | undefined,
  macroCategory: string | null | undefined
): string {
  // If we have a macro category, use its color
  if (macroCategory && categoryConfig[macroCategory]) {
    return categoryConfig[macroCategory].color;
  }
  // Otherwise, use typology-specific color
  return getTypologyColor(typology);
}

// Legacy exports for backward compatibility
// Use original keys instead of translated labels
export const typologyLabels: Record<string, string> = Object.fromEntries(
  Object.entries(typologyConfig).map(([key]) => [key, key]) // Use key itself, not translated label
);

export const typologyColorMap: Record<string, string> = Object.fromEntries(
  Object.entries(typologyConfig).map(([key, config]) => [key, config.color])
);
